import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { memo, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface FaqItem {
  question: string
  answer: string | ReactNode
}

const FaqItemComponent = memo(function FaqItemComponent({ question, answer }: FaqItem) {
  return (
    <Disclosure>
      {({ open }) => (
        <div className='border border-[#cecece] rounded-lg overflow-hidden'>
          <DisclosureButton className='w-full text-left px-4 py-4'>
            <div className='flex justify-between items-center w-full'>
              <p className='font-semibold text-base text-gray-12 leading-[1.6] flex-1'>
                {question}
              </p>
              <div className='flex items-center justify-center flex-shrink-0'>
                {open ? (
                  <ExpandLessIcon className='w-6 h-6 text-[#8d8d8d] transition-transform' />
                ) : (
                  <ExpandMoreIcon className='w-6 h-6 text-[#8d8d8d] transition-transform' />
                )}
              </div>
            </div>
          </DisclosureButton>
          <Transition
            enter='transition duration-100 ease-out'
            enterFrom='transform scale-95 opacity-0'
            enterTo='transform scale-100 opacity-100'
            leave='transition duration-75 ease-out'
            leaveFrom='transform scale-100 opacity-100'
            leaveTo='transform scale-95 opacity-0'
          >
            <DisclosurePanel>
              <div className='px-4 pb-4 font-normal text-sm text-gray-12 leading-[1.6] tracking-[-0.24px]'>
                {typeof answer === 'string' ? <p>{answer}</p> : answer}
              </div>
            </DisclosurePanel>
          </Transition>
        </div>
      )}
    </Disclosure>
  )
})

export default function DPFFaqJpSection() {
  const { t } = useTranslation('landingPage')
  const items = t('faq.items', { returnObjects: true })
  const faqItems: FaqItem[] = Array.isArray(items)
    ? items.map((item, index) => {
        const faqItem = item as { type?: 'gift' }

        return {
          question: t(`faq.items.${index}.question`),
          answer:
            faqItem.type === 'gift' ? (
              <>
                <p className='font-normal'>
                  {t('faq.giftAnswer.intro')}
                  <br />
                  {t('faq.giftAnswer.description')}
                </p>
                <br />
                <p>
                  <span className='font-semibold block'>{t('faq.giftAnswer.recipientTitle')}</span>
                  {t('faq.giftAnswer.recipientBody')}
                </p>
                <br />
                <p>
                  <span className='font-semibold block'>{t('faq.giftAnswer.senderTitle')}</span>
                  {t('faq.giftAnswer.senderBody')}
                </p>
              </>
            ) : (
              t(`faq.items.${index}.answer`)
            ),
        }
      })
    : []

  return (
    <section className='px-4 pt-10 pb-8'>
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2 items-center'>
          <h2 className='font-semibold text-2xl text-gray-12 text-center leading-[1.4]'>
            {t('faq.heading')}
          </h2>
        </div>
        <div className='flex flex-col gap-2'>
          {faqItems.map(item => (
            <FaqItemComponent key={item.question} question={item.question} answer={item.answer} />
          ))}
        </div>
      </div>
    </section>
  )
}
